import { useTooltip } from "@/components/tooltip/Tooltip";
import { displayHotkey } from "@/utils/hotkeys";
import { twMerge } from "@/utils/twMerge";

import { useCreateHintHoverHandlers } from "../hints/useHintHover";

import type { HintKey } from "@/store/HintsStore";
import type { Hotkey } from "@/utils/hotkeys";
import type { Placement } from "@floating-ui/react";
import type { ReactNode, ElementType, PropsWithChildren } from "react";

export type Props<T extends ElementType> = {
  icon?: React.ReactNode;
  title?: string;
  tooltipContent?: string;
  tooltipPlacement?: Placement;
  tooltipOffset?: number;
  isActive?: boolean;
  label?: string;
  disabled?: boolean;
  inverted?: boolean;
  className?: string;
  wrapperClassName?: string;
  hintKey?: HintKey;
  disableTooltip?: boolean;
  hotkey?: Hotkey;
  as?: T;
  ref?: React.Ref<React.ComponentRef<T>>;
} & React.ComponentPropsWithoutRef<T>;

export function Button<T extends ElementType = "button">({
  icon,
  label,
  className,
  title,
  tooltipContent,
  tooltipPlacement,
  tooltipOffset,
  disabled,
  inverted,
  isActive,
  as,
  children,
  wrapperClassName,
  hintKey,
  disableTooltip,
  hotkey,
  ...rest
}: PropsWithChildren<Props<T>>) {
  const Component = as || "button";
  const { handlerProps, tooltip } = useTooltip({
    title: hotkey ? <ButtonShortcut text={title} hotkey={hotkey} /> : title,
    content: tooltipContent,
    placement: tooltipPlacement,
    disabled: disabled || disableTooltip,
    offset: tooltipOffset,
  });
  const createHintHandlers = useCreateHintHoverHandlers();

  const ariaLabel = typeof title === "string" ? title : undefined;

  return (
    <>
      <Component
        aria-label={ariaLabel}
        disabled={disabled}
        className={twMerge(
          "relative flex flex-col w-full h-full min-h-9.5 max-w-9.5",
          "sm:min-w-12 sm:min-h-10 sm:rounded-none sm:hover:bg-transparent",
          "text-sm tracking-tight rounded-6",
          !!children && "max-w-full",
          // JS-conditional, not `disabled:` variant: the pseudo-class never matches `as="div"` toggles
          disabled && "opacity-40",
          !disabled &&
            "cursor-pointer hover:bg-sidebar-button-hover-bg group-data-[focus]:bg-sidebar-button-hover-bg",
          !disabled &&
            inverted &&
            "hover:bg-sidebar-button-hover-inverted-bg group-data-[focus]:bg-sidebar-button-hover-bg",
          isActive && "bg-sidebar-button-active-bg hover:bg-sidebar-button-active-bg",
          // As the content of a menu or popover button, it covers that button's inset focus ring.
          // Only a button parent: a focused menu panel must not ring every item in it
          "[button:focus-visible>&]:outline-solid [button:focus-visible>&]:outline-2 [button:focus-visible>&]:-outline-offset-2 [button:focus-visible>&]:outline-blue-500",
          className,
        )}
        {...rest}
      >
        <div
          {...handlerProps}
          {...(hintKey ? createHintHandlers(hintKey) : {})}
          className={twMerge(
            "h-full w-full flex-1 p-1.25 flex flex-col items-center justify-center",
            !!children && "flex-row",
            wrapperClassName,
          )}
        >
          {icon && <div className="flex-shrink-0">{icon}</div>}
          {label && !children && (
            <div className="text-button-label tracking-tight text-gray-500 text-center py-0.5">
              {label}
            </div>
          )}
          {children}
        </div>
      </Component>
      {tooltip}
    </>
  );
}

export function ButtonShortcut({ text, hotkey }: { text: ReactNode; hotkey?: Hotkey }) {
  return (
    <span className="flex items-center justify-between gap-4">
      <span>{text}</span>
      {hotkey ? <span className="text-gray-400 text-xxs">{displayHotkey(hotkey)}</span> : null}
    </span>
  );
}
